import { css } from '@emotion/react';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import {
  ContextMenuComponent, ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { type LatLng } from '~/_shared/types/latLng';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import { copyToClipboard } from '~/_shared/utils/clipboard/clipboard.helpers';
import { googleLatLngToLocal } from '~/_shared/utils/geolocation/geolocation';
import { useTranslation } from '~/_shared/utils/hooks';
import { DirectionSourceType } from '~/store/frontendState/mapTools/directions/directions.state';
import { DistanceCalculatorDrawingModeEnum } from '~/store/frontendState/mapTools/distanceCalculator/distanceCalculator.state';

const menuTriggerStyle = ({ x, y }: {x: number; y: number}) => css({
  position: 'absolute',
  top: y,
  left: x,
});

const menuItemStyle = css({
  fontSize: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
});

const submenuItemStyle = css({
  fontSize: '12px',
});

const moreInfoSubMenuStyle = css({
  width: '100%',
});

type MapContextMenuProps = Readonly<{
  address: string;
  googleMap: google.maps.Map;
  isAddDTPAllowed: boolean;
  isAddProximityCircleAllowed: boolean;
  isDistanceMeasuringAllowed: boolean;
  isAddMarkerAllowed: boolean;
  isFindClosestLocationAllowed: boolean;
  isGetDirectionsAllowed: boolean;
  isLoading: boolean;
  isStreetViewAvailable: boolean;
  latitude: number;
  longitude: number;

  addMarker: () => void;
  addProximityStart: (proximityType: ProximityType) => void;
  addWaypoint: (directionSourceType: DirectionSourceType) => void;
  findClosestLocation: () => void;
  getAddress: () => void;
  openStreetView: () => void;
  startDistanceCalculator: (mode: DistanceCalculatorDrawingModeEnum) => void;
  setAddress: (address: string) => void;
  setLatLng: (LatLng: LatLng) => void;
}>;

export const MapContextMenuComponent: FC<MapContextMenuProps> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [t] = useTranslation();
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const { addMarker, addWaypoint, addProximityStart, findClosestLocation,
    openStreetView, startDistanceCalculator, setLatLng } = props;

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleGetDirections = useCallback((directionSourceType: DirectionSourceType) =>
    () => addWaypoint(directionSourceType),
  [addWaypoint]);

  const copyCoordinatesToClipboard = () => {
    copyToClipboard(`${props.latitude}, ${props.longitude}`);
  };

  const copyAddressToClipboard = () => {
    copyToClipboard(props.address);
  };

  const handleDistanceCalculator = useCallback((mode: DistanceCalculatorDrawingModeEnum) =>
    () => startDistanceCalculator(mode),
  [startDistanceCalculator]);

  useEffect(() => {
    const listener = google.maps.event.addListener(props.googleMap, 'contextmenu', function (event: google.maps.MapMouseEvent) {
      setIsOpen(false);

      if (event.latLng) {
        setLatLng(googleLatLngToLocal(event.latLng));

        const mouseEvent = event.domEvent as MouseEvent;
        if (mouseEvent && mouseEvent.target && mouseEvent.clientX && mouseEvent.clientY) {
          const rect = (mouseEvent.target as HTMLElement).getBoundingClientRect();
          setMenuPosition({
            x: Math.floor((mouseEvent.clientX - rect.left) / (rect.right - rect.left) * rect.width),
            y: Math.floor((mouseEvent.clientY - rect.top) / (rect.bottom - rect.top) * rect.height),
          });

          setIsOpen(true);
        }
      }
    });

    return () => {
      setIsOpen(false);
      google.maps.event.removeListener(listener);
    };
  }, [props.googleMap, setLatLng]);

  return (
    <div>
      <ContextMenuComponent
        isVisible={isOpen}
        onHide={closeMenu}
        preferredPlacement="bottom-start"
        maxWidth={300}
        renderMenuContent={() => (
          <>
            {props.isLoading &&
              <OverlayLoaderComponent />
            }
            {props.isAddMarkerAllowed && (
              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  itemStyle={menuItemStyle}
                  onClick={addMarker}
                >
                  {t('Add Marker')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            )}

            {props.isAddProximityCircleAllowed && (
              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  itemStyle={menuItemStyle}
                  onClick={() => addProximityStart(ProximityType.DistanceRadius)}
                >
                  {t('Add Radius')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            )}

            {props.isAddDTPAllowed && (

              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  itemStyle={menuItemStyle}
                  onClick={() => addProximityStart(ProximityType.DriveTimePolygon)}
                >
                  {t('Add Drive Time Polygon')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            )}

            {props.isGetDirectionsAllowed && (
              <ContextMenuItemCoreComponent
                renderSubmenu={() => (
                  <>
                    <ContextMenuItemCoreComponent>
                      <ContextMenuDefaultItem
                        itemStyle={submenuItemStyle}
                        onClick={handleGetDirections(DirectionSourceType.From)}
                      >
                        {t('From Here')}
                      </ContextMenuDefaultItem>
                    </ContextMenuItemCoreComponent>
                    <ContextMenuItemCoreComponent>
                      <ContextMenuDefaultItem
                        itemStyle={submenuItemStyle}
                        onClick={handleGetDirections(DirectionSourceType.To)}
                      >
                        {t('To Here')}
                      </ContextMenuDefaultItem>
                    </ContextMenuItemCoreComponent>
                  </>
                )}
              >
                <ContextMenuDefaultItem
                  itemStyle={menuItemStyle}
                >
                  {t('Routing / Directions')}
                  <FontAwesomeIcon icon={faChevronRight} />
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            )}

            {props.isFindClosestLocationAllowed && (
              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  itemStyle={menuItemStyle}
                  onClick={findClosestLocation}
                >
                  {t('Find The Closest Locations')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            )}

            {props.isDistanceMeasuringAllowed && (
              <ContextMenuItemCoreComponent
                renderSubmenu={() => (
                  <>
                    <ContextMenuItemCoreComponent>
                      <ContextMenuDefaultItem
                        itemStyle={submenuItemStyle}
                        onClick={handleDistanceCalculator(DistanceCalculatorDrawingModeEnum.SinglePoint)}
                      >
                        {t('Single Point (A to B)')}
                      </ContextMenuDefaultItem>
                    </ContextMenuItemCoreComponent>
                    <ContextMenuItemCoreComponent>
                      <ContextMenuDefaultItem
                        itemStyle={submenuItemStyle}
                        onClick={handleDistanceCalculator(DistanceCalculatorDrawingModeEnum.MultiplePoint)}
                      >
                        {t('Multiple Point')}
                      </ContextMenuDefaultItem>
                    </ContextMenuItemCoreComponent>
                  </>
                )}
              >
                <ContextMenuDefaultItem itemStyle={menuItemStyle}>
                  {t('Measure Distance')}
                  <FontAwesomeIcon icon={faChevronRight} />
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            )}

            <ContextMenuItemCoreComponent
              submenuStyle={moreInfoSubMenuStyle}
              subMenuMaxWidth={300}
              renderSubmenu={() => (
                <>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      itemStyle={submenuItemStyle}
                    >
                      {t('Latitude')}: {props.latitude}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      itemStyle={submenuItemStyle}
                    >
                      {t('Longitude')}: {props.longitude}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      itemStyle={submenuItemStyle}
                    >
                      {t('Address')}: {props.address}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      itemStyle={submenuItemStyle}
                      onClick={openStreetView}
                      isDisabled={!props.isStreetViewAvailable}
                    >
                      {t(props.isStreetViewAvailable ? 'Show Street View' : 'Street View Not Available')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      itemStyle={submenuItemStyle}
                      onClick={copyAddressToClipboard}
                    >
                      {t('Copy Address to Clipboard')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      itemStyle={submenuItemStyle}
                      onClick={copyCoordinatesToClipboard}
                    >
                      {t('Copy Coordinates to Clipboard')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                </>
              )}
            >
              <ContextMenuDefaultItem itemStyle={menuItemStyle} >
                {t('More about location')}
                <FontAwesomeIcon icon={faChevronRight} />
              </ContextMenuDefaultItem>
            </ContextMenuItemCoreComponent>
          </>
        )}
      >
        {ref => (
          <span
            ref={ref}
            css={menuTriggerStyle(menuPosition)}
          />
        )}
      </ContextMenuComponent>
    </div>
  );
};
